import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@/components/Layout/ThemeProvider';
import { Layout } from '@/components/Layout/Layout';
import { AuthProvider } from '@/components/Auth/AuthProvider';

/** Updates canonical URL on every route change so Google indexes each page correctly */
function CanonicalUpdater() {
  const location = useLocation();
  useEffect(() => {
    const link = document.getElementById('canonical-link') as HTMLLinkElement | null;
    if (link) {
      link.href = `https://pastewala.com${location.pathname}`;
    }
  }, [location.pathname]);
  return null;
}

// Lazy-loaded pages — each becomes its own chunk
// Named exports mapped to default for React.lazy compatibility
const HomePage = lazy(() => import('@/pages/HomePage').then(m => ({ default: m.HomePage })));
const PasteViewPage = lazy(() => import('@/pages/PasteViewPage').then(m => ({ default: m.PasteViewPage })));
const PasswordGatePage = lazy(() => import('@/pages/PasswordGatePage').then(m => ({ default: m.PasswordGatePage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const LiveSessionPage = lazy(() => import('@/pages/LiveSessionPage').then(m => ({ default: m.LiveSessionPage })));
const DiffPage = lazy(() => import('@/pages/DiffPage').then(m => ({ default: m.DiffPage })));
const EmbedPage = lazy(() => import('@/pages/EmbedPage').then(m => ({ default: m.EmbedPage })));
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const AdminPage = lazy(() => import('@/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const CollectionsPage = lazy(() => import('@/pages/CollectionsPage').then(m => ({ default: m.CollectionsPage })));
const CollectionDetailPage = lazy(() => import('@/pages/CollectionDetailPage').then(m => ({ default: m.CollectionDetailPage })));
const BlogPage = lazy(() => import('@/pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const PSeoPage = lazy(() => import('@/pages/PSeoPage').then(m => ({ default: m.PSeoPage })));
const ComparePage = lazy(() => import('@/pages/ComparePage').then(m => ({ default: m.ComparePage })));
const CompareIndexPage = lazy(() => import('@/pages/ComparePage').then(m => ({ default: m.CompareIndexPage })));
const TermsPage = lazy(() => import('@/pages/legal/TermsPage').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const DmcaPage = lazy(() => import('@/pages/legal/DmcaPage').then(m => ({ default: m.DmcaPage })));

function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '40vh',
      color: 'var(--text-muted)',
      fontSize: '15px',
    }}>
      Loading...
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CanonicalUpdater />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Embed page — no navbar/layout */}
            <Route path="/embed/:idOrSlug" element={<EmbedPage />} />

            {/* All other pages wrapped in Layout */}
            <Route
              path="*"
              element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/collections" element={<CollectionsPage />} />
                      <Route path="/collections/:id" element={<CollectionDetailPage />} />
                      <Route path="/blog" element={<BlogPage />} />
                      <Route path="/blog/:slug" element={<BlogPostPage />} />
                      <Route path="/diff" element={<DiffPage />} />
                      {/* Programmatic SEO landing pages */}
                      <Route path="/share/:slug" element={<PSeoPage category="language" />} />
                      <Route path="/use/:slug" element={<PSeoPage category="use-case" />} />
                      <Route path="/format/:slug" element={<PSeoPage category="format" />} />
                      <Route path="/compare" element={<CompareIndexPage />} />
                      <Route path="/compare/:slug" element={<ComparePage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/dmca" element={<DmcaPage />} />
                      <Route path="/live/:sessionId" element={<LiveSessionPage />} />
                      <Route path="/:idOrSlug/unlock" element={<PasswordGatePage />} />
                      <Route path="/:idOrSlug/analytics" element={<AnalyticsPage />} />
                      <Route path="/:idOrSlug" element={<PasteViewPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </Layout>
              }
            />
          </Routes>
        </Suspense>
      </AuthProvider>
    </ThemeProvider>
  );
}
